import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Tabs, 
  DialogActions,
  DialogContent,
} from '@mui/material';
import { Button, CollapsingTabScrollButton, BhiDialog } from 'src/UIKit';
import { useSelector } from 'react-redux';
import ReactRouterPrompt from 'react-router-prompt';
import { selectProfile } from 'src/modules/profile/slices';
import { isActionAllowed } from 'src/helpers/permissions';
import CompanyInfo from '../Company/view';
import UserManagment from '../UserManagement/view';
import MessageTemplates from '../MessageTemplates/view';
import CustomFields from '../CustomFields/view';
import AppointmentTypes from '../AppointmentTypes/view';
import { IntegrationsView } from '../IntegrationsView';
import { ApiKeysView } from '../ApiKeysView';
import { useNavigateWithCompany } from 'src/hooks/useNavigateWithCompany';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { LinkTab } from 'src/components/LinkTab';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useHandleSelectedCompany } from 'src/hooks/useHandleSelectedCompany';
import { ContactImportLogsView } from 'src/modules/contacts/components';

type TabPanelProps = {
  children?: React.ReactNode;
  name: string;
  value: string;
};

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, name, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== name}
      id={`simple-tabpanel-${name}`}
      aria-labelledby={`simple-tab-${name}`}
      sx={{ mt: 2 }}
      {...other}
    >
      {value === name && <>{children}</>}
    </Box>
  );
};

const getProps = (name: string) => {
  return {
    id: `simple-tab-${name}`,
    'aria-controls': `simple-tabpanel-${name}`,
  };
};

export const SettingsView = () => {
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigateWithCompany = useNavigateWithCompany();
  const { profile } = useSelector(selectProfile);
  const profileRole = useMemo(() => (profile?.role ? profile.role.name : 'bhi_admin'), [profile]);
  const [searchParams] = useSearchParams();
  
  // Use the hook to handle company state properly across tabs
  useHandleSelectedCompany();
  
  // Prioritize URL company ID for tab isolation, fallback to Redux/localStorage
  const urlCompanyId = searchParams.get('companyId');
  const reduxCompanyId = useSelector(getCurrentCompanyId);
  const currentCompanyId = urlCompanyId || reduxCompanyId || localStorage.getItem('bhiCurrentCompany') || '';

  // Define available tabs based on user permissions
  const availableTabs = useMemo(() => {
    const tabs = ['companies', 'messageTemplates', 'contactFields', 'appointmentTypes', 'contactImports'];

    if (isActionAllowed({ authorRole: profileRole })) {
      tabs.splice(1, 0, 'users'); // Add 'users' after 'companies'
      tabs.push('webhooks', 'integrations');
    }

    return tabs;
  }, [profileRole]);

  // Get initial tab from URL parameter or default to 'companies'
  const getInitialTab = () => {
    if (urlTab && availableTabs.includes(urlTab)) {
      return urlTab;
    }
    return 'companies';
  };

  const [tab, setTab] = useState(getInitialTab());
  const [isCompanyFormDirty, setIsCompanyFormDirty] = useState(false);

  // Update tab when URL parameter changes
  useEffect(() => {
    if (urlTab && availableTabs.includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    } else if (!urlTab && tab !== 'companies') {
      setTab('companies');
    }
  }, [urlTab, availableTabs, tab]);

  // Reset dirty state when navigating away from companies tab
  useEffect(() => {
    if (tab !== 'companies' && isCompanyFormDirty) {
      setIsCompanyFormDirty(false);
    }
  }, [tab, isCompanyFormDirty]);

  // Update URL when tab changes
  const handleTabChange = (event: React.SyntheticEvent, newTab: string) => {
    if (tab === 'companies' || isCompanyFormDirty) {
      return;
    }

    setTab(newTab);
    navigateWithCompany(`/settings/${newTab}`);
  };

  if (!profile || !currentCompanyId) {
    return null;
  }

  return (
    <>
      <ReactRouterPrompt
        when={isCompanyFormDirty}
      >
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <BhiDialog open={true} title="You have unsaved changes!">
              <DialogContent>Do you want to continue without saving?</DialogContent>
              <DialogActions>
                <Button onClick={onCancel} color="primary">
                  No
                </Button>
                <Button 
                  onClick={onConfirm} 
                  color="secondary"
                >
                  Yes
                </Button>
              </DialogActions>
            </BhiDialog>
          )
        }
      </ReactRouterPrompt>
      <Box sx={{ width: '100%' }}>
        <Tabs
          ScrollButtonComponent={CollapsingTabScrollButton}
          scrollButtons="auto"
          value={tab}
          variant="scrollable"
          onChange={handleTabChange}
          aria-label="personal information tab"
        >
          <LinkTab
            label="Company"
            value="companies"
            {...getProps('companies')}
            to={generateUrlWithCompany('/settings/companies', currentCompanyId)}
          />
          {isActionAllowed({ authorRole: profileRole }) && (
            <LinkTab
              label="User Management"
              value="users"
              {...getProps('users')}
              to={generateUrlWithCompany('/settings/users', currentCompanyId)}
            />
          )}
          <LinkTab
            label="Message Templates"
            value="messageTemplates"
            {...getProps('messageTemplates')}
            to={generateUrlWithCompany('/settings/messageTemplates', currentCompanyId)}
          />
          <LinkTab
            label="Contact Fields"
            value="contactFields"
            {...getProps('contactFields')}
            to={generateUrlWithCompany('/settings/contactFields', currentCompanyId)}
          />
          <LinkTab
            label="Contact Imports"
            value="contactImports"
            {...getProps('contactImports')}
            to={generateUrlWithCompany('/settings/contactImports', currentCompanyId)}
          />
          <LinkTab
            label="Calendars"
            value="appointmentTypes"
            {...getProps('appointmentTypes')}
            to={generateUrlWithCompany('/settings/appointmentTypes', currentCompanyId)}
          />
          {isActionAllowed({ authorRole: profileRole }) && (
            <LinkTab
              label="Webhook Integrations"
              value="webhooks"
              {...getProps('webhooks')}
              to={generateUrlWithCompany('/settings/webhooks', currentCompanyId)}
            />
          )}
          {isActionAllowed({ authorRole: profileRole }) && (
            <LinkTab
              label="API Integrations"
              value="integrations"
              {...getProps('integrations')}
              to={generateUrlWithCompany('/settings/integrations', currentCompanyId)}
            />
          )}
        </Tabs>
        <CustomTabPanel value={tab} name="companies">
          <Box sx={{ width: '100%' }}>
            <CompanyInfo onUnsavedChangesChange={setIsCompanyFormDirty} />
          </Box>
        </CustomTabPanel>
        {isActionAllowed({ authorRole: profileRole }) && (
          <CustomTabPanel value={tab} name="users">
            <Box sx={{ width: '100%' }}>
              <UserManagment />
            </Box>
          </CustomTabPanel>
        )}
        <CustomTabPanel value={tab} name="messageTemplates">
          <MessageTemplates />
        </CustomTabPanel>
        <CustomTabPanel value={tab} name="contactFields">
          <Box sx={{ width: '100%' }}>
            <CustomFields profileRole={profileRole} isCompanyFields={false} showButtons={true} />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={tab} name="appointmentTypes">
          <Box sx={{ width: '100%' }}>
            <AppointmentTypes />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={tab} name="contactImports">
          <ContactImportLogsView />
        </CustomTabPanel>
        <CustomTabPanel value={tab} name="webhooks">
          <Box sx={{ width: '100%' }}>
            <IntegrationsView profileRole={profileRole} />
          </Box>
        </CustomTabPanel>
        {isActionAllowed({ authorRole: profileRole }) && (
          <CustomTabPanel value={tab} name="integrations">
            <Box sx={{ width: '100%' }}>
              <ApiKeysView profileRole={profileRole} />
            </Box>
          </CustomTabPanel>
        )}
      </Box>
    </>
  );
};
